const API_URL = import.meta.env.VITE_API_URL || 'https://api.pumperlens.com/api';

export interface AuthResponse {
  message: string;
  user: {
    id: number;
    email: string;
  };
}

export interface LoginData {
  email: string;
  password: string;
}

export interface SignupData extends LoginData {
  confirmPassword?: string;
}

export interface GoogleAuthData {
  google_id: string;
  email: string;
}

class AuthService {
  private async getCSRFToken(): Promise<string> {
    const response = await fetch(`${API_URL}/auth/csrf/`, {
      credentials: 'include',
    });
    const data = await response.json();
    return data.csrfToken;
  }

  private async getHeaders(): Promise<HeadersInit> {
    const csrfToken = await this.getCSRFToken();
    return {
      'Content-Type': 'application/json',
      'X-CSRFToken': csrfToken,
    };
  }

  async login(data: LoginData): Promise<AuthResponse> {
    const headers = await this.getHeaders();
    const response = await fetch(`${API_URL}/auth/login/`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to login');
    }

    return response.json();
  }

  async signup(data: SignupData): Promise<AuthResponse> {
    const { confirmPassword, ...signupData } = data;
    const headers = await this.getHeaders();
    const response = await fetch(`${API_URL}/auth/signup/`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(signupData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to sign up');
    }

    return response.json();
  }

  async googleAuth(googleUser: any): Promise<AuthResponse> {
    const headers = await this.getHeaders();
    const response = await fetch(`${API_URL}/auth/google-auth/`, {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        google_id: googleUser.sub,
        email: googleUser.email,
        name: googleUser.name,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Google authentication failed');
    }

    const data = await response.json();
    return data;
  }

  async logout(): Promise<void> {
    const headers = await this.getHeaders();
    const response = await fetch(`${API_URL}/auth/logout/`, {
      method: 'POST',
      headers,
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to logout');
    }
  }

  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    const headers = await this.getHeaders();
    const response = await fetch(`${API_URL}/auth/change-password/`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ old_password: oldPassword, new_password: newPassword }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to change password');
    }
  }

  async deleteAccount(confirmation: string): Promise<void> {
    const headers = await this.getHeaders();
    const response = await fetch(`${API_URL}/auth/delete-account/`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ confirm: confirmation }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete account');
    }
  }

  async getCurrentUser(): Promise<{ email: string; is_google_user: boolean }> {
    const headers = await this.getHeaders();
    const response = await fetch(`${API_URL}/auth/current-user/`, {
      method: 'GET',
      headers,
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get user information');
    }

    return response.json();
  }
}

export const authService = new AuthService(); 